import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
    Compass, ArrowRight, Sparkles, Plus, Car, Home as HomeIcon,
    Baby, GraduationCap, Plane, Briefcase, TrendingUp, FileText, Package,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const ICON_MAP = {
    Car, Home: HomeIcon, Sparkles, Baby, GraduationCap,
    Plane, Briefcase, TrendingUp, FileText, Package,
};

const TONE = {
    sky: "text-sky-700 bg-sky-50 border-sky-200",
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-200",
    amber: "text-amber-800 bg-amber-50 border-amber-200",
    rose: "text-rose-700 bg-rose-50 border-rose-200",
    violet: "text-violet-700 bg-violet-50 border-violet-200",
    teal: "text-teal-700 bg-teal-50 border-teal-200",
    indigo: "text-indigo-700 bg-indigo-50 border-indigo-200",
    stone: "text-stone-700 bg-stone-100 border-stone-200",
    orange: "text-orange-800 bg-orange-50 border-orange-200",
};

const KIND_LABEL = {
    timeline: "Life timeline event",
    asset: "Asset",
    renewal: "Renewal reminder",
    vehicle: "Vehicle Hub entry",
    home_project: "Home Hub project",
    task: "Task",
};

export default function Journeys() {
    const { hasRole } = useAuth();
    const canEdit = hasRole && hasRole("admin", "finance", "expense_user");
    const nav = useNavigate();

    const [templates, setTemplates] = useState([]);
    const [rows, setRows] = useState([]);
    const [openTemplate, setOpenTemplate] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);

    const [form, setForm] = useState({
        title: "",
        start_date: new Date().toISOString().slice(0, 10),
        budget: "",
        currency: "INR",
        notes: "",
    });

    const load = useCallback(async () => {
        try {
            const [t, r] = await Promise.all([
                api.get("/journeys/templates"),
                api.get("/journeys"),
            ]);
            setTemplates(t.data.templates || []);
            setRows(r.data || []);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    const startPreview = (tmpl) => {
        setOpenTemplate(tmpl);
        setForm({
            title: tmpl.title,
            start_date: new Date().toISOString().slice(0, 10),
            budget: "",
            currency: "INR",
            notes: "",
        });
        setPreview(null);
    };

    const generatePreview = async () => {
        if (!openTemplate) return;
        setBusy(true);
        try {
            const body = {
                template_key: openTemplate.key,
                title: form.title,
                start_date: form.start_date,
                budget: form.budget ? Number(form.budget) : null,
                currency: form.currency,
                notes: form.notes || null,
            };
            const { data } = await api.post("/journeys/preview", body);
            setPreview(data);
            setPreviewOpen(true);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const toggleItem = (idx) => {
        setPreview((old) => {
            if (!old) return old;
            const items = old.items.map((it, i) => i === idx ? { ...it, enabled: !it.enabled } : it);
            return { ...old, items };
        });
    };

    const editItemLabel = (idx, label) => {
        setPreview((old) => {
            if (!old) return old;
            const items = old.items.map((it, i) => i === idx ? { ...it, label } : it);
            return { ...old, items };
        });
    };

    const commit = async () => {
        if (!openTemplate || !preview) return;
        setBusy(true);
        try {
            const body = {
                template_key: openTemplate.key,
                title: form.title,
                start_date: form.start_date,
                budget: form.budget ? Number(form.budget) : null,
                currency: form.currency,
                notes: form.notes || null,
                items: preview.items,
            };
            const { data } = await api.post("/journeys", body);
            toast.success("Journey started — everything wired up.");
            setPreviewOpen(false);
            setOpenTemplate(null);
            setPreview(null);
            load();
            nav(`/journeys/${data.id}`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBusy(false);
        }
    };

    const active = useMemo(() => rows.filter((r) => r.status === "active"), [rows]);
    const done = useMemo(() => rows.filter((r) => r.status !== "active"), [rows]);

    return (
        <div data-testid="journeys-page">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                <div>
                    <div className="eyebrow flex items-center gap-1.5"><Compass size={12} /> Life Moments</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                        Journeys.
                    </h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                        Pick a life moment and VAULT wires up the whole picture &mdash; asset, warranties,
                        renewals, timeline, budget hints and a task checklist &mdash; in one review step.
                    </p>
                </div>
            </div>

            {active.length > 0 && (
                <section className="mb-10" data-testid="journeys-active">
                    <div className="eyebrow mb-4">Active journeys · {active.length}</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {active.map((j) => {
                            const Icon = ICON_MAP[j.icon] || Compass;
                            const tone = TONE[j.color] || TONE.stone;
                            return (
                                <Link
                                    key={j.id}
                                    to={`/journeys/${j.id}`}
                                    className="group rounded-xl border border-stone-200 bg-white p-5 hover:border-stone-900 transition-colors"
                                    data-testid={`journey-active-${j.id}`}
                                >
                                    <div className="flex items-start gap-3 mb-4">
                                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${tone}`}>
                                            <Icon size={16} />
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm font-medium text-stone-900 truncate">{j.title}</div>
                                            <div className="text-[11px] uppercase tracking-widest text-stone-500 mt-0.5">
                                                {j.template_title} · Started {fmtDate(j.start_date)}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between text-[12px] mb-1.5">
                                        <span className="text-stone-500">
                                            {j.task_done}/{j.task_total} tasks
                                        </span>
                                        <span className="font-mono-figure text-stone-900">{j.progress_pct}%</span>
                                    </div>
                                    <div className="h-1.5 rounded-full bg-stone-100 overflow-hidden">
                                        <div
                                            className="h-full bg-stone-900 transition-all"
                                            style={{ width: `${j.progress_pct}%` }}
                                        />
                                    </div>
                                    <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-stone-900 group-hover:gap-2.5 transition-all">
                                        Open <ArrowRight size={11} />
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                </section>
            )}

            <section className="mb-10" data-testid="journeys-templates">
                <div className="eyebrow mb-4">Start a new journey</div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {templates.map((t) => {
                        const Icon = ICON_MAP[t.icon] || Compass;
                        const tone = TONE[t.color] || TONE.stone;
                        return (
                            <button
                                key={t.key}
                                type="button"
                                onClick={() => canEdit && startPreview(t)}
                                disabled={!canEdit}
                                className={`text-left rounded-xl border p-5 transition-all ${
                                    canEdit
                                        ? "border-stone-200 bg-white hover:border-stone-900 hover:-translate-y-0.5"
                                        : "border-stone-200 bg-stone-50 opacity-60 cursor-not-allowed"
                                }`}
                                data-testid={`journey-template-${t.key}`}
                            >
                                <div className="flex items-start gap-3 mb-3">
                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${tone}`}>
                                        <Icon size={16} />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-medium text-stone-900">{t.title}</div>
                                        <div className="text-[10px] uppercase tracking-widest text-stone-500 mt-0.5">
                                            {t.creates.length} auto-created
                                        </div>
                                    </div>
                                </div>
                                <p className="text-[12px] text-stone-600 leading-relaxed line-clamp-2">
                                    {t.tagline}
                                </p>
                                <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-stone-900">
                                    Preview & start <ArrowRight size={11} />
                                </div>
                            </button>
                        );
                    })}
                </div>
            </section>

            {done.length > 0 && (
                <section data-testid="journeys-done">
                    <div className="eyebrow mb-4">Completed · {done.length}</div>
                    <div className="rounded-lg border border-stone-200 bg-white divide-y divide-stone-100">
                        {done.map((j) => (
                            <Link key={j.id} to={`/journeys/${j.id}`} className="flex items-center justify-between px-5 py-3 hover:bg-stone-50">
                                <div className="text-sm text-stone-900">{j.title}</div>
                                <div className="text-[11px] uppercase tracking-widest text-stone-500">
                                    {j.progress_pct}% · {fmtDate(j.start_date)}
                                </div>
                            </Link>
                        ))}
                    </div>
                </section>
            )}

            {/* Start dialog — collects title/date/budget then generates preview */}
            <Dialog open={!!openTemplate && !previewOpen} onOpenChange={(o) => { if (!o) setOpenTemplate(null); }}>
                <DialogContent className="max-w-lg" data-testid="journey-start-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl">
                            {openTemplate?.title}
                        </DialogTitle>
                        <DialogDescription className="text-[13px] text-stone-500">
                            {openTemplate?.tagline}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div>
                            <Label className="eyebrow">Title *</Label>
                            <Input
                                required
                                value={form.title}
                                onChange={(e) => setForm({ ...form, title: e.target.value })}
                                className="mt-1.5"
                                placeholder="e.g. Tesla Model 3, Bengaluru home"
                                data-testid="journey-form-title"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label className="eyebrow">Start date</Label>
                                <Input
                                    type="date"
                                    value={form.start_date}
                                    onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                                    className="mt-1.5"
                                    data-testid="journey-form-date"
                                />
                            </div>
                            <div>
                                <Label className="eyebrow">Budget (optional)</Label>
                                <Input
                                    type="number"
                                    value={form.budget}
                                    onChange={(e) => setForm({ ...form, budget: e.target.value })}
                                    className="mt-1.5 font-mono-figure"
                                    placeholder="in ₹"
                                    data-testid="journey-form-budget"
                                />
                            </div>
                        </div>
                        <div>
                            <Label className="eyebrow">Notes (optional)</Label>
                            <Textarea
                                rows={2}
                                value={form.notes}
                                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                className="mt-1.5"
                                placeholder="Any context that helps you remember this journey later"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpenTemplate(null)}>Cancel</Button>
                        <Button
                            onClick={generatePreview}
                            disabled={busy || !form.title}
                            className="bg-stone-900 hover:bg-stone-800 text-white"
                            data-testid="journey-form-preview-btn"
                        >
                            <Sparkles size={13} className="mr-1.5" /> Preview fan-out
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Preview dialog — user unchecks / edits before commit */}
            <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
                <DialogContent className="max-w-2xl" data-testid="journey-preview-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl">
                            We&rsquo;ll create the following
                        </DialogTitle>
                        <DialogDescription className="text-[13px] text-stone-500">
                            Uncheck anything you don&rsquo;t want, or edit the label. Nothing is saved yet.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="max-h-[55vh] overflow-y-auto thin-scroll -mx-6 px-6 space-y-2">
                        {preview?.items?.map((it, i) => (
                            <div
                                key={i}
                                className={`flex items-start gap-3 rounded-lg border p-3 ${
                                    it.enabled ? "border-stone-200 bg-white" : "border-stone-200 bg-stone-50 opacity-60"
                                }`}
                                data-testid={`journey-preview-item-${i}`}
                            >
                                <Checkbox
                                    checked={it.enabled}
                                    onCheckedChange={() => toggleItem(i)}
                                    data-testid={`journey-preview-toggle-${i}`}
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-1">
                                        {KIND_LABEL[it.kind] || it.kind}
                                    </div>
                                    <Input
                                        value={it.label}
                                        onChange={(e) => editItemLabel(i, e.target.value)}
                                        className="border-0 shadow-none px-0 focus-visible:ring-0 text-sm text-stone-900 h-auto py-0"
                                    />
                                    {it.kind === "renewal" && it.payload?.expiry_date && (
                                        <div className="text-[11px] text-stone-500 mt-0.5">
                                            Reminder set for {fmtDate(it.payload.expiry_date)}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                    <DialogFooter className="border-t border-stone-100 pt-4">
                        <Button variant="outline" onClick={() => setPreviewOpen(false)}>Back</Button>
                        <Button
                            onClick={commit}
                            disabled={busy}
                            className="bg-stone-900 hover:bg-stone-800 text-white"
                            data-testid="journey-commit-btn"
                        >
                            <Plus size={13} className="mr-1.5" /> Start journey
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
